/**
 * 지역 — **정규 목록은 여기 하나다.**
 *
 * 전에는 웹의 필터 목록과 기업마당 프리셋의 허용목록이 따로 살았고, 그 사이에서
 * `전남광주` 가 태어났다. 정부 API 는 관할기관을 `전남광주통합특별시` 로 보내지만
 * 독자의 필터는 광주·전남으로 나뉜다 — 그래서 둘로 푼다.
 */
export const REGIONS = [
    '서울', '부산', '대구', '인천', '광주', '대전', '울산', '세종',
    '경기', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주',
];
export const REGION_NAMES = {
    서울: '서울특별시', 부산: '부산광역시', 대구: '대구광역시', 인천: '인천광역시',
    광주: '광주광역시', 대전: '대전광역시', 울산: '울산광역시', 세종: '세종특별자치시',
    경기: '경기도', 강원: '강원특별자치도', 충북: '충청북도', 충남: '충청남도',
    전북: '전북특별자치도', 전남: '전라남도', 경북: '경상북도', 경남: '경상남도',
    제주: '제주특별자치도',
};
/** 긴 이름·옛 이름·통합명 → 정규 코드. 정규 코드 자체는 표에 없어도 통과한다. */
const ALIASES = {
    서울특별시: ['서울'], 부산광역시: ['부산'], 대구광역시: ['대구'], 인천광역시: ['인천'],
    광주광역시: ['광주'], 대전광역시: ['대전'], 울산광역시: ['울산'],
    세종특별자치시: ['세종'], 세종시: ['세종'],
    경기도: ['경기'], 강원도: ['강원'], 강원특별자치도: ['강원'],
    충청북도: ['충북'], 충청남도: ['충남'],
    전라북도: ['전북'], 전북특별자치도: ['전북'], 전라남도: ['전남'],
    경상북도: ['경북'], 경상남도: ['경남'],
    제주도: ['제주'], 제주특별자치도: ['제주'],
    전남광주: ['광주', '전남'], 전남광주통합특별시: ['광주', '전남'],
};
const ORDER = new Map(REGIONS.map((r, i) => [r, i]));
export function canonicalRegions(token) {
    const t = token.trim();
    if (ORDER.has(t))
        return [t];
    return [...(ALIASES[t] ?? [])];
}
/** 정규 코드만 남기고 `REGIONS` 순으로 정렬·중복 제거. 모르는 값은 조용히 떨어진다. */
export function sortRegions(list) {
    const seen = new Set();
    for (const v of list)
        for (const r of canonicalRegions(v))
            seen.add(r);
    return [...seen].sort((a, b) => ORDER.get(a) - ORDER.get(b));
}
/** 권역명 → 시도. 말머리·태그에 실제로 나오는 것만 둔다. */
export const ZONES = {
    수도권: ['서울', '인천', '경기'],
    비수도권: ['부산', '대구', '광주', '대전', '울산', '세종', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'],
    충청권: ['대전', '세종', '충북', '충남'],
    호남권: ['광주', '전북', '전남'],
    동남권: ['부산', '울산', '경남'],
    대경권: ['대구', '경북'],
    영남권: ['부산', '울산', '경남', '대구', '경북'],
};
const LEAD_TAG = /^\s*\[([^\]]*)\]\s*/;
/** 태그 안의 구분자. ㆍ(U+318D) ·(U+00B7) ‧(U+2027) ・(U+30FB) 쉼표 슬래시 「및」 공백. */
const TAG_SPLIT = /[ㆍ·‧・,/]|\s+및\s+|\s+/;
/**
 * 선두 말머리를 떼고, 그 안의 지역 표현을 시도 집합으로 푼다.
 *
 * 괄호 안의 낱말이 **모두** 지역·권역·전국으로 풀릴 때만 지역 정보로 쓴다.
 * 하나라도 안 풀리면 (`[경기도 중장년 최초 창업 지원]`) 지역이 아니다.
 * 어느 쪽이든 제목에서는 뗀다 — 말머리는 소스가 붙인 표기이지 제목이 아니다.
 */
export function splitLeadTag(title) {
    let rest = title;
    const found = [];
    let nationwide = false;
    for (let m = LEAD_TAG.exec(rest); m; m = LEAD_TAG.exec(rest)) {
        rest = rest.slice(m[0].length);
        const tokens = m[1].split(TAG_SPLIT).map((t) => t.trim()).filter(Boolean);
        if (tokens.length === 0)
            continue;
        const resolved = [];
        let sawNationwide = false;
        let ok = true;
        for (const t of tokens) {
            if (t === '전국') {
                sawNationwide = true;
                continue;
            }
            const zone = ZONES[t];
            const rs = zone ? [...zone] : canonicalRegions(t);
            if (rs.length === 0) {
                ok = false;
                break;
            }
            resolved.push(...rs);
        }
        if (!ok)
            continue;
        found.push(...resolved);
        if (sawNationwide)
            nationwide = true;
    }
    const cleaned = rest.trim();
    return {
        title: cleaned.length > 0 ? cleaned : title.trim(),
        regions: nationwide ? [] : sortRegions(found),
        nationwide,
    };
}
/** 화면의 지역 문구. 전국 → `전국`, 둘까지 나열, 넘치면 `외`. 모르면 null. */
export function regionLabel(g, opts = {}) {
    if (g.nationwide)
        return '전국';
    const max = opts.max ?? 2;
    const rs = g.supportRegions;
    if (rs.length === 0)
        return null;
    if (rs.length <= max)
        return rs.join('·');
    return `${rs.slice(0, max).join('·')} 외`;
}
/**
 * 자격 행 `kind: "region"` 의 코드표 (설계 2026-10-02 §2).
 *
 * 시도 17 + 권역 7 + `전국`. **해석이 지역을 정하는 통로다** — 소스 태그가
 * 틀렸을 때(기업마당이 경북 공고에 17개 시도를 전부 단 실측) 공고문이 이긴다.
 */
export const REGION_CODES = [...REGIONS, ...Object.keys(ZONES), '전국'];
export const isRegionCode = (v) => typeof v === 'string' && REGION_CODES.includes(v);
/** 권역 → 시도. `전국` 은 빈 목록 — 행이 아니라 플래그다. */
export function expandRegionCode(code) {
    if (code === '전국')
        return [];
    const zone = ZONES[code];
    return zone ? [...zone] : [code];
}
/**
 * 저장된 자격 행에서 공고의 지역을 정한다.
 *
 * 코드 붙은 region 행이 없으면 `null` — 호출부는 아무것도 바꾸지 않는다.
 * `전국` 이 하나라도 있으면 전국이다. 「강원 소재」와 「전국 제조기업」이 같이
 * 있으면 넓은 쪽이 맞다 — 좁게 틀리면 신청할 수 있는 사람이 못 본다.
 */
export function regionsFromEligibility(rows) {
    const codes = rows.filter((r) => r.kind === 'region' && isRegionCode(r.code)).map((r) => r.code);
    if (codes.length === 0)
        return null;
    if (codes.includes('전국'))
        return { nationwide: true, regions: [] };
    return { nationwide: false, regions: sortRegions(codes.flatMap(expandRegionCode)) };
}
/**
 * 문구가 코드와 **다른** 시도를 이름으로 부르나. 「강원특별자치도 소재」에 `경북` 이
 * 붙는 것을 막는다. 시·군 이름은 모른다 — 「구미 소재」는 검사하지 않는다.
 * `전국` · 권역 코드는 검사하지 않는다 — 「전국 제조기업 (강원 소재 우대)」가 있다.
 */
export function namesOtherRegion(code, text) {
    if (code === '전국' || ZONES[code])
        return false;
    let rest = text.replace(/\s+/g, '');
    const named = new Set();
    // 별칭을 먼저 떼야 「경기도」의 「경기」를 두 번 세지 않는다. 별칭끼리는 겹치지 않는다.
    // 통합명(「전남광주통합특별시」)은 두 시도라 코드 하나로 못 덮는다 — 둘 다 「다른 시도」로 센다.
    for (const [name, rs] of Object.entries(ALIASES)) {
        if (!rest.includes(name))
            continue;
        rs.forEach((r) => named.add(r));
        rest = rest.split(name).join('');
    }
    for (const r of REGIONS)
        if (rest.includes(r))
            named.add(r);
    named.delete(code);
    return named.size > 0;
}
//# sourceMappingURL=regions.js.map